import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  Container,
  useMediaQuery,
  Box,
  Button,
  TextareaAutosize,
  TextField,
  InputLabel,
  OutlinedInput,
} from "@mui/material";
import "../../styles/inputBox.css";
import { useNavigate, useParams } from "react-router-dom";
import { putRequest } from "../../api/apiServices";
import { updateJob } from "../../api/apiList";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { roleBasedRedirection } from "../../utils/helper";
import {
  buildJobProfileFourthSnapshot,
  isSelfEmittedFormUpdate,
} from "../../utils/jobSpecFormSync";
import Loader from "../Loader";

interface JobProfilefourthProps {
  onBack: () => void;
  onNext: () => void; // Function to navigate to the next component
  setFormData: (data: any) => void;
  initialData: any;
  mode: any;
}

const JobProfilefourth: React.FC<JobProfilefourthProps> = ({
  onBack,
  onNext,
  setFormData,
  initialData,
  mode,
}: JobProfilefourthProps) => {
  const isMobile = useMediaQuery("(max-width:578px)");
  const isTabs = useMediaQuery("(max-width:1200px)");

  // filed decalre
  const [teamInteraction, setTeamInteraction] = useState("");
  const [reportingStructure, setReportingStructure] = useState("");
  const [cultureFit, setCultureFit] = useState("");
  const [loading, setLoading] = useState(false);

  const lastEmittedSnapshotRef = useRef<string | null>(null);
  const hasHydratedRef = useRef(false);
  const loadedJobIdRef = useRef<string | number | null>(null);

  //params to get id
  const params = useParams();
  const navigate = useNavigate();

  const goBack = () => {
    // navigate("/jobs");
    navigate(roleBasedRedirection("/jobs"));
  };

  const [errors, setErrors] = useState({
    teamInteraction: "",
    reportingStructure: "",
    cultureFit: "",
  });

  // Sync local state with initialData when loading a job or external updates only
  useEffect(() => {
    if (!initialData || Object.keys(initialData).length === 0) {
      return;
    }

    const incomingSnapshot = buildJobProfileFourthSnapshot(initialData);
    if (isSelfEmittedFormUpdate(incomingSnapshot, lastEmittedSnapshotRef)) {
      hasHydratedRef.current = true;
      return;
    }

    const jobId = initialData.id as string | number | undefined;
    const isNewExternalJobLoad =
      jobId != null && loadedJobIdRef.current !== jobId;
    const shouldHydrate = isNewExternalJobLoad || !hasHydratedRef.current;

    if (!shouldHydrate) {
      return;
    }

    if (jobId != null) {
      loadedJobIdRef.current = jobId;
    }

    if (initialData?.teamInteraction !== undefined) {
      setTeamInteraction(initialData.teamInteraction || "");
    }
    if (initialData?.reportingStructure !== undefined) {
      setReportingStructure(initialData.reportingStructure || "");
    }
    if (initialData?.cultureFit !== undefined) {
      setCultureFit(initialData.cultureFit || "");
    }

    hasHydratedRef.current = true;
  }, [initialData]);

  // Update formData whenever any field changes
  useEffect(() => {
    if (!hasHydratedRef.current) {
      return;
    }

    const hasChanges = 
      teamInteraction !== (initialData?.teamInteraction || "") ||
      reportingStructure !== (initialData?.reportingStructure || "") ||
      cultureFit !== (initialData?.cultureFit || "");

    if (hasChanges) {
      const currentData = {
        ...initialData,
        teamInteraction,
        reportingStructure,
        cultureFit,
      };
      lastEmittedSnapshotRef.current = buildJobProfileFourthSnapshot(currentData);
      setFormData(currentData);
    }
  }, [teamInteraction, reportingStructure, cultureFit, initialData, setFormData]);

  const handleBack = (event: any) => {
    event.preventDefault();
    onBack();
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    const data = {
      teamInteraction,
      reportingStructure,
      cultureFit,
    };
    if (formIsValid()) {
      if (mode) {
        setLoading(true);
        const response = await putRequest(updateJob(params?.id), data);
        setFormData(response?.data?.data);
        setLoading(false);
        onNext();
      } else {
        const allData = {
          ...initialData,
          ...data,
        };
        setFormData(allData);
        onNext();
      }
    }
  };

  const formIsValid = () => {
    const newErrors = {
      teamInteraction: !teamInteraction?.trim()
        ? "Team structure is required"
        : "",
      reportingStructure: !reportingStructure?.trim()
        ? "Reporting Structure is required"
        : "",
      cultureFit: !cultureFit?.trim() ? "Team Culture Fit is required" : "",
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  return (
    <div>
      <Container
        style={{
          display: "flex",
          padding: "25px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid
          spacing={2}
          bgcolor="#FFFFFF"
          width="884px"
          borderRadius="22px"
          border="1px solid #D6D6D7"
          margin={0}
        >
          <Grid
            item
            height={isMobile ? "auto" : "auto"}
            style={{ padding: 30 }}
          >
            <Grid
              container
              direction="row"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap="10px"
            >
              <Grid
                item
                width="fit-content"
                display="flex"
                alignItems="center"
                gap="10px"
              >
                <ArrowBackIcon onClick={goBack} style={{ cursor: "pointer" }} />
                <Typography color="#273848" fontWeight={700} fontSize="20.94px">
                  Team & Culture
                </Typography>
              </Grid>
              <Grid item gap={2} display="flex">
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#84E1CC",
                  }}
                />
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#84E1CC",
                  }}
                />
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#84E1CC",
                  }}
                />
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#84E1CC",
                  }}
                />
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#DFF4EF",
                  }}
                />
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#DFF4EF",
                  }}
                />
              </Grid>
            </Grid>
            <Grid mt="26px"></Grid>

            {/* <Grid
              display="flex"
              alignItems="center"
              border="1px solid #c3c3c3"
              borderRadius="8px"
              padding="8px 0px 6px 15px"
            >
              <Grid direction="row" display="flex" gap={1} alignItems="center">
                <img src={TeamIcon} alt="team-icon" />
                <Typography
                  //   className="input-label-custom"
                  fontWeight={700}
                  fontSize="13.94px"
                  lineHeight="13.94px"
                  color="#273848"
                >
                  Team & Culture
                </Typography>
              </Grid>
            </Grid> */}

            <Grid mt="22px"></Grid>
            <Typography
              color="#273848"
              fontWeight={700}
              lineHeight="14.94px"
              fontSize="15px"
              //   className="input-label-custom"
            >
              Team Structure
            </Typography>
            <Grid mt="19px"></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                How is the team structured for this role?{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <OutlinedInput
                placeholder="How is the team structured for this role?"
                multiline
                minRows={4}
                maxRows={10}
                className="text-area-size"
                value={teamInteraction}
                onChange={(e) => {
                  const value = e.target.value;
                  setTeamInteraction(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      teamInteraction: "",
                    }));
                  }
                }}
              />
              <InputLabel
                style={{
                  color: "#f44336",
                  paddingTop: "3px",
                  fontSize: "12px",
                  paddingLeft: "10px",
                }}
              >
                {errors.teamInteraction}
              </InputLabel>
            </Grid>
            <Grid mt="25px"></Grid>
            <Typography
              color="#273848"
              fontWeight={700}
              lineHeight="14.94px"
              fontSize="15px"
              //   className="input-label-custom"
            >
              Reporting Structure
            </Typography>
            <Grid mt="19px"></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                To whom will the candidate report?{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <OutlinedInput
                placeholder="To whom will the candidate report?"
                multiline
                minRows={4}
                maxRows={10}
                className="text-area-size"
                value={reportingStructure}
                onChange={(e) => {
                  const value = e.target.value;
                  setReportingStructure(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      reportingStructure: "",
                    }));
                  }
                }}
              />
              <InputLabel
                style={{
                  color: "#f44336",
                  paddingTop: "3px",
                  fontSize: "12px",
                  paddingLeft: "10px",
                }}
              >
                {errors.reportingStructure}
              </InputLabel>
            </Grid>
            <Grid mt="24px"></Grid>
            <Typography
              color="#273848"
              fontWeight={700}
              lineHeight="14.94px"
              fontSize="15px"
              //   className="input-label-custom"
            >
              Team Culture Fit
            </Typography>
            <Grid mt="19px"></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                What is the team culture like, and what attributes should the
                candidate possess to fit in well?{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <OutlinedInput
                placeholder="What is the team culture like, and what attributes should the candidate possess to fit in well?"
                fullWidth
                multiline
                minRows={4}
                maxRows={10}
                className="input-box-custom"
                error={!!errors.cultureFit}
                value={cultureFit}
                onChange={(e) => {
                  const value = e.target.value;
                  setCultureFit(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      cultureFit: "",
                    }));
                  }
                }}
              />
            </Grid>
            <Grid mt="40px"></Grid>
            <Grid
              container
              direction="row"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid xs={isTabs ? 0 : 2}>
                <button
                  type="submit"
                  className="user-scale"
                  // variant="contained"
                  // fullWidth
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #000000",
                    color: "#273848",
                    boxShadow: "none",
                    borderRadius: "5px",
                    // padding: "10px 50px",
                    padding: "17px 17px",
                    lineHeight: "0px",
                    letterSpacing: "0px",
                    textTransform: "none",
                    width: " 100%",
                  }}
                  onClick={(event) => handleBack(event)}
                >
                  Back
                </button>
              </Grid>
              <Grid
                item
                xs={isTabs ? 0 : mode ? 3 : 2}
                style={{ maxWidth: "fitContent" }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  style={{
                    backgroundColor: "#84E1CC",
                    boxShadow: "none",
                    borderRadius: "5px",
                    padding: loading ? "7px" : "17px 17px",
                    lineHeight: "0px",
                    textTransform: "none",
                    letterSpacing: "0px",
                  }}
                  onClick={(event) => handleSubmit(event)}
                >
                  {mode ? loading ? <Loader /> : "Save & Next" : "Continue"}
                </Button>
              </Grid>
            </Grid>
            <Grid mb="20px"></Grid>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default JobProfilefourth;
